// employeesSection/index.tsx
//
// Aba "Colaboradores": quem tem acesso ao sistema, em qual grupo, e o que ainda
// não dá para configurar.
//
// O QUE ESTAVA AQUI, e por que a aba abria uma TELA BRANCA:
// as quatro sub-telas liam `subsidiary.employees` da travessia do InfoToken.
// Essa chave NÃO EXISTE — uma filial no token tem `subsidiaryInfo`, `ambients`,
// `periodTypes` e `periods`, e mais nada. `undefined.map(...)` lança, o React
// desmonta a árvore inteira e a página fica em branco. Não era erro de dado: era
// a aba derrubando o aplicativo, e já era assim antes deste card — as fotos do
// gabarito "antes" mostram a mesma tela branca.
//
// Sobre "Horários" e "Espelho de Ponto": saíram do menu (§9.5/§20.4). Não havia
// entidade, nem rota, nem coluna — só o mesmo `map` sobre `undefined`. Menu que
// derruba a tela é pior do que menu sem a entrada.
//
// De onde os colaboradores vêm agora: `GET /employees`, a única rota paginada
// do backend. Ela respondia 400 para qualquer `?page=`/`?limit=` porque a
// querystring chega como TEXTO e ia crua para o `take` do Prisma — corrigido no
// handler, junto com um teto de 200 por página.

import React, { useCallback, useEffect, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/employeesSection";
import api from "../../../../services/api.service";

interface EmployeesSectionProps {
  selectedVerticalOption: string;
  isEditing: boolean;
  formData: any;
  decryptedInfoToken: any;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  handleSave: (objectType: any, payload?: any) => any;
  updateState: (newState: any) => void;
}

interface Colaborador {
  userUuid: string;
  userName?: string;
  userSurname?: string;
  userSocialName?: string;
  userEmail?: string;
  userPhoneNumber?: string;
  userStatus?: boolean;
  createdAt?: string;
  userGroupUuid?: { userGroupName?: string; userGroupUuid?: string };
}

const POR_PAGINA = 10;

/**
 * O aviso que sustenta a honestidade desta aba.
 *
 * O card pede vínculo usuário × loja × papel, o campo "atendente" e a ALÇADA
 * (quem aprova o quê, até que valor). O vínculo e o "atendente" cabem em
 * `ActUserGroup`/`ActUserGroupInfo`, que já existem — falta a rota. A alçada
 * NÃO cabe: exige as entidades `Alcada` e `Aprovacao`, que este card não criou.
 *
 * Coletar um limite de aprovação que não é gravado em lugar nenhum seria pior
 * do que não ter o campo: o gerente olharia a tela, veria "teto R$ 500" e
 * confiaria num limite que ninguém aplica. Por isso a tela DECLARA a
 * dependência em vez de desenhar o formulário.
 */
const O_QUE_FALTA = [
  {
    titulo: "Vínculo com a loja e papel",
    texto:
      "O usuário hoje pertence a um GRUPO da rede, não a uma loja. Vincular " +
      'usuário × loja × papel (e marcar quem é atendente) cabe em acd."ActUserGroupInfo", ' +
      "que já existe no banco — falta a rota que a expõe.",
  },
  {
    titulo: "Alçada de aprovação",
    texto:
      "Quem aprova o quê, e até que valor, depende das entidades Alcada e " +
      "Aprovacao, que ainda não existem no banco. A regra já decidida — gerente " +
      "aprova tudo da loja dele, sem teto para o que é da própria loja — está " +
      "registrada e vale quando as tabelas nascerem. Nenhum limite de aprovação " +
      "está valendo hoje.",
  },
];

const EmployeesSection: React.FC<EmployeesSectionProps> = ({
  selectedVerticalOption,
  handleSave,
}) => {
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const carregar = useCallback((paginaAlvo: number) => {
    // Guarda de unmount: sem ela, trocar de aba enquanto a resposta está no ar
    // faz o `setState` cair num componente desmontado.
    let ativo = true;
    setCarregando(true);
    setErro(null);

    api
      .get(`employees?page=${paginaAlvo}&limit=${POR_PAGINA}`)
      .then(({ data }) => {
        if (!ativo) return;
        setColaboradores(data?.employees ?? []);
        setTotal(data?.pagination?.total ?? 0);
        setCarregando(false);
      })
      .catch((erroDaRede) => {
        if (!ativo) return;
        setColaboradores([]);
        setCarregando(false);
        setErro(
          erroDaRede?.response?.data?.message ??
            "Não foi possível carregar a lista de colaboradores."
        );
      });

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => carregar(pagina), [carregar, pagina]);

  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const nomeDe = (pessoa: Colaborador) =>
    [pessoa.userSocialName || pessoa.userName, pessoa.userSurname]
      .filter(Boolean)
      .join(" ") || "—";

  const pedirGravacao = async (tipo: "employee" | "access") => {
    const resultado: any = await handleSave(tipo);
    if (resultado) setAviso({ tom: resultado.tom, texto: resultado.texto });
  };

  const cabecalho = (titulo: string, subtitulo: string) => (
    <AlfredoStyles.Header>
      <AlfredoStyles.HeaderTitle>
        <AlfredoStyles.MaterialIcons>badge</AlfredoStyles.MaterialIcons>
        {titulo}
      </AlfredoStyles.HeaderTitle>
      <AlfredoStyles.HeaderSubtitle>{subtitulo}</AlfredoStyles.HeaderSubtitle>
    </AlfredoStyles.Header>
  );

  const listaDeColaboradores = () => {
    if (carregando) {
      return (
        <AlfredoStyles.Vazio>
          <AlfredoStyles.MaterialIcons>
            hourglass_top
          </AlfredoStyles.MaterialIcons>
          Carregando colaboradores…
        </AlfredoStyles.Vazio>
      );
    }

    if (erro) {
      return (
        <AlfredoStyles.Aviso $tom="erro">
          <strong>Não foi possível carregar.</strong> {erro}
        </AlfredoStyles.Aviso>
      );
    }

    if (colaboradores.length === 0) {
      return (
        <AlfredoStyles.Vazio>
          <AlfredoStyles.MaterialIcons>person_off</AlfredoStyles.MaterialIcons>
          Nenhum colaborador cadastrado nesta rede.
        </AlfredoStyles.Vazio>
      );
    }

    return (
      <>
        <AlfredoStyles.Tabela>
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Telefone</th>
              <th>Grupo</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {colaboradores.map((pessoa) => (
              <tr key={pessoa.userUuid}>
                <td>{nomeDe(pessoa)}</td>
                <td>{pessoa.userEmail || "—"}</td>
                <td>{pessoa.userPhoneNumber || "—"}</td>
                <td>{pessoa.userGroupUuid?.userGroupName || "—"}</td>
                <td>
                  <AlfredoStyles.Selo $ativo={pessoa.userStatus !== false}>
                    {pessoa.userStatus !== false ? "Ativo" : "Inativo"}
                  </AlfredoStyles.Selo>
                </td>
              </tr>
            ))}
          </tbody>
        </AlfredoStyles.Tabela>

        <AlfredoStyles.Paginacao>
          <AlfredoStyles.Button
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina <= 1}
          >
            <AlfredoStyles.MaterialIcons>
              chevron_left
            </AlfredoStyles.MaterialIcons>
            Anterior
          </AlfredoStyles.Button>
          <span>
            Página {pagina} de {paginas} · {total} colaborador(es)
          </span>
          <AlfredoStyles.Button
            onClick={() => setPagina((p) => Math.min(paginas, p + 1))}
            disabled={pagina >= paginas}
          >
            Próxima
            <AlfredoStyles.MaterialIcons>
              chevron_right
            </AlfredoStyles.MaterialIcons>
          </AlfredoStyles.Button>
        </AlfredoStyles.Paginacao>
      </>
    );
  };

  const blocoDoQueFalta = (tipo: "employee" | "access") => (
    <>
      {O_QUE_FALTA.map((item) => (
        <AlfredoStyles.Aviso key={item.titulo} $tom="atencao">
          <strong>{item.titulo}.</strong> {item.texto}
        </AlfredoStyles.Aviso>
      ))}
      <AlfredoStyles.ButtonWrapper>
        <AlfredoStyles.Button onClick={() => pedirGravacao(tipo)}>
          <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
          Tentar salvar mesmo assim
        </AlfredoStyles.Button>
      </AlfredoStyles.ButtonWrapper>
      {aviso && (
        <AlfredoStyles.Aviso $tom={aviso.tom}>{aviso.texto}</AlfredoStyles.Aviso>
      )}
    </>
  );

  switch (selectedVerticalOption) {
    case "Equipe":
      return (
        <AlfredoStyles.Container>
          {cabecalho(
            "Equipe",
            "Quem tem acesso ao sistema desta rede, direto do cadastro de usuários."
          )}
          {listaDeColaboradores()}
          {blocoDoQueFalta("employee")}
        </AlfredoStyles.Container>
      );

    case "Acessos":
      return (
        <AlfredoStyles.Container>
          {cabecalho(
            "Acessos",
            "O grupo de cada pessoa é o que hoje define o que ela alcança."
          )}
          <AlfredoStyles.Aviso $tom="atencao">
            <strong>A permissão de hoje é grossa.</strong> O backend distingue
            administrador de não-administrador e, para os demais, confere uma
            linha de rota + verbo HTTP no grupo do usuário. Não existe permissão
            por capacidade (ver custo, aprovar compra, editar preço) — o que
            aparece abaixo é o grupo, não a alçada.
          </AlfredoStyles.Aviso>
          {listaDeColaboradores()}
          {blocoDoQueFalta("access")}
        </AlfredoStyles.Container>
      );

    default:
      return (
        <AlfredoStyles.Vazio>
          <AlfredoStyles.MaterialIcons>search</AlfredoStyles.MaterialIcons>
          Sessão não encontrada: {selectedVerticalOption}
        </AlfredoStyles.Vazio>
      );
  }
};

export default EmployeesSection;
